import React from 'react';
import { useFormContext, get, FieldValues, PathValue, Path } from 'react-hook-form';
import { FormComponentProps } from '../types';

interface FormTimeFieldProps<T> extends FormComponentProps<T> {
  title: string;
  mandatory?: boolean;
  showSeconds?: boolean;
  hideErrorControl?: boolean;
}

export const FormTimeField = <T extends FieldValues>(props: FormTimeFieldProps<T>) => {
  const {
    formState: { errors },
    setValue,
    getValues
  } = useFormContext<T>();

  const errorForName = React.useMemo(() => {
    return get(errors, props.name);
  }, [errors, props.name]);

  const currentValue = getValues(props.name);
  const [initialHour, initialMinute, initialSecond] = currentValue
    ? currentValue.match(/.{1,2}/g)
    : ['00', '00', '00'];

  const [hour, setHour] = React.useState(initialHour);
  const [minute, setMinute] = React.useState(initialMinute);
  const [second, setSecond] = React.useState(initialSecond);

  React.useMemo(() => {
    setValue(props.name, `${hour}${minute}${second}` as PathValue<T, Path<T>>);
  }, [hour, minute, props.name, second, setValue]);

  const handleHourChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (parseInt(value) > 23 || parseInt(value) < 0) return;
    setHour(value.substring(0, 2));
  };

  const handleMinuteChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMinute(value.substring(0, 2));
  };

  const handleSecondChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSecond(value.substring(0, 2));
  };

  const backgroundColour = () => {
    if (props.readonly) {
      return 'bg-transparent';
    } else if (errorForName) {
      return 'bg-feedback-dark-red';
    } else {
      return 'bg-interactive-bold-green-db-12';
    }
  };

  const borderColour = () => {
    if (props.readonly) {
      return 'border-transparent';
    } else if (errorForName) {
      return 'border-b-feedback-red';
    } else {
      return 'border-b-solid-bold-green';
    }
  };

  const labelTextColour = () => {
    if (props.readonly) {
      return 'text-solid-white';
    } else if (errorForName) {
      return 'text-feedback-red';
    } else {
      return 'text-solid-bold-green';
    }
  };

  const errorControl = () => {
    const error = errorForName;
    if (!error) {
      return <br />;
    }
    return <label className="text-feedback-red text-xs">{error.message?.toString()}</label>;
  };
  return (
    <div className={`w-full`}>
      <div
        className={`cursor-text ${backgroundColour()} ${borderColour()} pl-3 border-b-2 pt-2 pb-2 text-solid-white placeholder-shown:text-secondary-deep-blue-30`}
      >
        <div className="flex flex-row justify-between pr-3">
          <label className={`cursor-text text-xs ${labelTextColour()} block`}>{props.title}</label>
          {props.mandatory && !props.readonly && (
            <label className={`text-xs ${labelTextColour()} block`}>*</label>
          )}
        </div>
        <div className="flex flex-row justify-start">
          <input
            readOnly={props.readonly}
            className={`${backgroundColour()} focus:outline-none w-1/5 text-start`}
            type="text"
            placeholder={'00'}
            value={hour}
            onChange={handleHourChanged}
          />
          {':'}
          <input
            readOnly={props.readonly}
            className={`${backgroundColour()} focus:outline-none w-[30%] text-center`}
            type="text"
            placeholder={'00'}
            value={minute}
            onChange={handleMinuteChanged}
          />
          {props.showSeconds && (
            <>
              {':'}
              <input
                readOnly={props.readonly}
                className={`${backgroundColour()} focus:outline-none w-1/5 text-end`}
                type="text"
                placeholder={'00'}
                value={second}
                onChange={handleSecondChanged}
              />
            </>
          )}
        </div>
      </div>
      {!props.hideErrorControl && <div>{errorControl()}</div>}
    </div>
  );
};
